import { useState } from 'react';
import { DateInput, FORM_CONTROL_CLASS, FORM_LABEL_CLASS, FORM_TEXTAREA_CLASS, Modal } from '@safetyhub/ui';
import {
  formatDateISO,
  formatObligationInterval,
  formatObligationRecurrence,
  topicParticleOf,
  withObjectParticle,
  type LegalObligationIntervalVariantView,
  type ObligationRecurrenceRule,
} from '@safetyhub/shared';
import { EditModalFooter } from '../common/EditModalFooter';
import { fulfilledAtToIso } from './EvidenceModal';
import { todayDateInputInTimezone } from '../../lib/localDate';

const NOTE_MAX_LENGTH = 500;

export interface LastPerformedInput {
  /** UTC ISO — 실시일(또는 기산일) 현장 시간대 00:00:00 */
  performedAt: string;
  note?: string;
  /** 설치·신설·배치일(`CYCLE_START`)이면 명시 — 없으면 마지막 실시일 */
  kind?: 'CYCLE_START';
  /** 기산일 입력이 고른 최초 간격 변형(`INITIAL` · `EVENT_CHOICE`) */
  intervalVariantId?: string;
}

export interface LastPerformedModalProps {
  open: boolean;
  obligationTitle: string;
  /** 유효 주기 규칙 — 「2년마다(전후 6개월)」 같은 안내와 기산일 입구 여부(최초 간격이 있나)를 가른다 */
  rule: ObligationRecurrenceRule;
  /** 기산일 입력의 선택지(`target = INITIAL` · `selector = EVENT_CHOICE`) — 없으면 빈 배열 */
  variants: LegalObligationIntervalVariantView[];
  /** 현장이 이미 넣은 마지막 실시일(로컬 `yyyy-MM-dd`) — 정정이면 초기값 */
  initialDate: string | null;
  /** 현장이 이미 넣은 설치·신설·배치일(로컬 `yyyy-MM-dd`) */
  initialCycleStartDate: string | null;
  /** 그 기산일 입력이 고른 변형 id */
  initialCycleStartVariantId: string | null;
  /** 현장 유효 시간대(서버 `LegalObligationsResponse.timezone`) — 실시일 변환과 「오늘」(2026-11-03 P2) */
  timezone: string;
  submitting: boolean;
  errorMessage: string | null;
  onSubmit: (input: LastPerformedInput) => void;
  onClose: () => void;
}

/** UTC ISO → **현장 시간대** `yyyy-MM-dd` — 정정 초기값(종전 브라우저 로컬 · 2026-11-03 P2) */
export function isoToLocalDate(iso: string, timezone: string): string {
  return formatDateISO(new Date(iso), timezone);
}

/**
 * 기준일 입력·정정(9-l · ②-b D6) — 「기준일부터 주기가 도는」 문서의 다음 기한 = 그날 + 간격. 입력 전에는 목록이
 * 「마지막 실시일 확인 필요」로 그린다. 문서를 등록하면 그 이행일이 다음 주기의 기준이 되므로 **처음 한 번만** 넣으면 된다
 * (서버의 유효 기준일 = 입력 ∨ 최신 이행일 중 늦은 쪽).
 *
 * 카탈로그에 **최초 간격**이 있는 문서(안전검사 「설치 후 3년」 · 특수건강진단 「배치 후 1~12개월」 · 근골격계 「신설 1년」)는
 * 「설치·신설·배치일」 입구가 함께 열린다 — 아직 한 번도 실시하지 않은 현장은 그 날을 넣으면 첫 기한이 선다. 실시일이 있으면
 * 서버가 실시일을 기준으로 삼는다(기산일은 첫 주기에만 쓰인다).
 */
export default function LastPerformedModal({
  open,
  obligationTitle,
  rule,
  variants,
  initialDate,
  initialCycleStartDate,
  initialCycleStartVariantId,
  timezone,
  submitting,
  errorMessage,
  onSubmit,
  onClose,
}: LastPerformedModalProps) {
  const cycleStartAvailable = rule.initialInterval !== null || variants.length > 0;
  const [kind, setKind] = useState<'LAST_PERFORMED' | 'CYCLE_START'>(
    cycleStartAvailable && initialDate === null && initialCycleStartDate === null ? 'CYCLE_START' : 'LAST_PERFORMED',
  );
  const isCycleStart = kind === 'CYCLE_START';
  const initialFor = isCycleStart ? initialCycleStartDate : initialDate;
  const [performedOn, setPerformedOn] = useState(initialFor ?? '');
  const [variantId, setVariantId] = useState(initialCycleStartVariantId ?? '');
  const [note, setNote] = useState('');
  const trimmed = note.trim();
  const isDirty = performedOn !== (initialFor ?? '') || trimmed !== '' || variantId !== (initialCycleStartVariantId ?? '');
  const dateLabel = isCycleStart ? '설치·신설·배치일' : '마지막 실시일';
  const blockedReason = !performedOn ? `${withObjectParticle(dateLabel)} 선택하세요.` : '';
  const recurrenceText = formatObligationRecurrence({ recurrence: rule.recurrence, recurrenceRule: rule }) ?? '기준일부터 주기가 도는';
  const chosenInitial = variants.find((v) => v.id === variantId)?.interval ?? rule.initialInterval;

  const switchKind = (next: 'LAST_PERFORMED' | 'CYCLE_START') => {
    setKind(next);
    setPerformedOn((next === 'CYCLE_START' ? initialCycleStartDate : initialDate) ?? '');
  };

  const handleSubmit = () => {
    if (blockedReason || submitting) return;
    onSubmit({
      performedAt: fulfilledAtToIso(performedOn, timezone),
      ...(trimmed ? { note: trimmed } : {}),
      ...(isCycleStart ? { kind: 'CYCLE_START' as const } : {}),
      ...(isCycleStart && variantId ? { intervalVariantId: variantId } : {}),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCycleStart ? '설치·신설·배치일' : '마지막 실시일'}
      size="md"
      closeLocked={submitting}
      isDirty={isDirty}
      footer={
        <EditModalFooter
          isDirty={isDirty}
          blockedReason={blockedReason}
          submitLabel="저장"
          submitDisabled={!!blockedReason || submitting || !isDirty}
          busy={submitting}
          busyLabel="저장 중..."
          onSubmit={handleSubmit}
          onFallbackClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-ink-700">
          <span className="font-bold text-ink-900">{obligationTitle}</span>
          {topicParticleOf(obligationTitle)} {recurrenceText} 하는 문서입니다.{' '}
          {isCycleStart
            ? `설치·신설·배치한 날을 입력하면 첫 기한(그날부터 ${chosenInitial ? formatObligationInterval(chosenInitial) : formatObligationInterval(rule.interval ?? { unit: 'YEAR', count: 1 })})이 정해집니다.`
            : '이 현장에서 마지막으로 실시한 날을 입력하면 다음 기한이 정해집니다.'}
        </p>
        {cycleStartAvailable && (
          <div role="radiogroup" aria-label="기준일 종류" className="flex gap-4 text-sm text-ink-700">
            <label className="inline-flex items-center gap-1">
              <input type="radio" name="legal-anchor-kind" checked={!isCycleStart} onChange={() => switchKind('LAST_PERFORMED')} disabled={submitting} />
              마지막 실시일
            </label>
            <label className="inline-flex items-center gap-1">
              <input type="radio" name="legal-anchor-kind" checked={isCycleStart} onChange={() => switchKind('CYCLE_START')} disabled={submitting} />
              설치·신설·배치일 (아직 실시한 적 없음)
            </label>
          </div>
        )}
        <div>
          <label htmlFor="legal-last-performed-on" className={FORM_LABEL_CLASS}>
            {dateLabel}
          </label>
          <DateInput
            id="legal-last-performed-on"
            label={dateLabel}
            value={performedOn}
            onChange={setPerformedOn}
            max={todayDateInputInTimezone(timezone)}
            disabled={submitting}
            className="w-full"
          />
          <p className="mt-1 text-xs text-ink-500">
            이후 문서를 등록하면 그 이행일부터 다음 주기가 자동으로 계산됩니다.
          </p>
        </div>
        {isCycleStart && variants.length > 0 && (
          <div>
            <label htmlFor="legal-cycle-start-variant" className={FORM_LABEL_CLASS}>
              해당 사항
            </label>
            <select
              id="legal-cycle-start-variant"
              className={FORM_CONTROL_CLASS}
              value={variantId}
              onChange={(e) => setVariantId(e.target.value)}
              disabled={submitting}
            >
              <option value="">
                해당 없음{rule.initialInterval ? ` (기본 — 첫 기한 ${formatObligationInterval(rule.initialInterval)})` : ''}
              </option>
              {variants.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label ?? formatObligationInterval(v.interval)} (첫 기한 {formatObligationInterval(v.interval)})
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label htmlFor="legal-last-performed-note" className={FORM_LABEL_CLASS}>
            메모 (선택)
          </label>
          <textarea
            id="legal-last-performed-note"
            className={FORM_TEXTAREA_CLASS}
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            placeholder="예: 2024년 3월 외부 기관 조사 결과서 기준"
          />
        </div>
        {errorMessage && (
          <p
            role="alert"
            className="rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
          >
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
